/**
 * Out-of-React-state, ownership-scoped holder for the registration proof
 * across its whole page lifecycle (capture → lookup/completion → the
 * CLMS-137 first-credential follow-up's status → user-activated redeem).
 * React double-buffers fibers, so a secret placed in immutable state or
 * props survives in the `alternate` tree after a state "scrub"; this
 * mutable module-level slot is never part of any fiber's hook state or
 * props, so alternate-fiber retention is structurally impossible.
 *
 * Ownership: `hold` mints an opaque owner token and starts a new
 * generation. `read`/`clear` act only when the presented token matches the
 * current generation, so a stale flow (unmounted page, late-settling
 * callback) can neither read nor clear a newer flow's proof. Nothing here
 * ever touches the URL, storage, or caches.
 */

/** Opaque, non-secret ownership token minted by holdFirstCredentialProof. */
export type FirstCredentialProofOwner = symbol;

let heldProof: string | null = null;
let currentOwner: FirstCredentialProofOwner | null = null;

/** Parks the proof and transfers holder ownership to the returned token. */
export function holdFirstCredentialProof(proof: string): FirstCredentialProofOwner {
  const owner: FirstCredentialProofOwner = Symbol("first-credential-proof-owner");
  heldProof = proof;
  currentOwner = owner;
  return owner;
}

/** Non-consuming, ownership-gated read: a stale token reads null. */
export function readFirstCredentialProof(owner: FirstCredentialProofOwner | null): string | null {
  return owner !== null && owner === currentOwner ? heldProof : null;
}

/** Synchronous ownership-gated scrub: a stale token's clear is a no-op. */
export function clearFirstCredentialProof(owner: FirstCredentialProofOwner | null): void {
  if (owner !== null && owner === currentOwner) {
    heldProof = null;
    currentOwner = null;
  }
}

/** Test-only unscoped peek so suites can assert holder contents. */
export function peekFirstCredentialProofForTests(): string | null {
  return heldProof;
}

/** Test-only unscoped reset for suite setup/teardown isolation. */
export function resetFirstCredentialProofHolderForTests(): void {
  heldProof = null;
  currentOwner = null;
}
